/**
 * Defensive extraction helpers over the raw Enin bundle payloads.
 *
 * The exact live shapes of the bundle endpoints are an acknowledged open item
 * (see extractEninStatus in @repo/shared/types/enin) — like there, every
 * picker probes a list of candidate field names and degrades to null/empty
 * instead of throwing. The raw-data accordion in the result panel is the
 * verification tool for refining these candidate lists against real payloads.
 */

import type { EninLookupBundle } from "./flow-instances";

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

/** Payload as rows: array as-is, else the first array found under a wrapper key. */
function unwrapRows(payload: unknown, wrapperKeys: string[]): unknown[] {
  if (Array.isArray(payload)) return payload;
  if (isRecord(payload)) {
    for (const key of wrapperKeys) {
      const value = payload[key];
      if (Array.isArray(value)) return value;
    }
  }
  return [];
}

function firstRecord(payload: unknown, wrapperKeys: string[]): Record<string, unknown> | null {
  if (isRecord(payload)) return payload;
  const rows = unwrapRows(payload, wrapperKeys);
  const first = rows.find(isRecord);
  return first ?? null;
}

/** Resolve a candidate key against obj; "a.b" probes one nesting level. */
function resolve(obj: Record<string, unknown>, candidate: string): unknown {
  if (!candidate.includes(".")) return obj[candidate];
  const [head, tail] = candidate.split(".", 2);
  const nested = obj[head as string];
  return isRecord(nested) ? nested[tail as string] : undefined;
}

function pickString(obj: Record<string, unknown> | null, candidates: string[]): string | null {
  if (!obj) return null;
  for (const candidate of candidates) {
    const value = resolve(obj, candidate);
    if (typeof value === "string" && value.trim() !== "") return value;
  }
  return null;
}

function pickNumber(obj: Record<string, unknown> | null, candidates: string[]): number | null {
  if (!obj) return null;
  for (const candidate of candidates) {
    const value = resolve(obj, candidate);
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
      const n = Number(value);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

// ─── Company details (header extras) ─────────────────────────────────────────

export type EninCompanyDetails = {
  name: string | null;
  orgForm: string | null;
  employees: number | null;
  founded: string | null;
};

export function extractCompanyDetails(payload: EninLookupBundle["company_details"]) {
  const record = firstRecord(payload, ["company_details_collection", "data", "company"]);
  const details: EninCompanyDetails = {
    name: pickString(record, ["name", "company.name", "company_name"]),
    orgForm: pickString(record, [
      "organization_form",
      "org_form_code",
      "company_type",
      "org_form",
      "company.org_form_code",
    ]),
    employees: pickNumber(record, ["number_of_employees", "employees", "employee_count"]),
    founded: pickString(record, [
      "founded_date",
      "establishment_date",
      "registration_date",
      "company.founded_date",
    ]),
  };
  return details;
}

// ─── Financial highlights ────────────────────────────────────────────────────

export type EninFinancials = {
  year: number | null;
  revenue: number | null;
  operatingProfit: number | null;
  netIncome: number | null;
  totalAssets: number | null;
  equity: number | null;
  /** balance__cash_and_deposits — feeds the FS "Fill from ENIN" asset pre-fill. */
  cashAndDeposits: number | null;
  /** balance__liabilities — total debt (not split by maturity). */
  liabilities: number | null;
};

// Live accounts-highlights payload (verified 2026-07 against a NO company):
// a flat array of records with `income_statement__*` / `balance__*` prefixed
// keys plus `accounting_year`. The unprefixed candidates are kept as
// fallbacks for other schema versions.
export function extractFinancials(payload: EninLookupBundle["accounting_highlights"]) {
  const record = firstRecord(payload, ["accounts_highlights_collection", "data", "accounts"]);
  const financials: EninFinancials = {
    year: pickNumber(record, ["accounting_year", "accounts.accounting_year", "year"]),
    revenue: pickNumber(record, [
      "income_statement__operating_revenue",
      "operating_revenue",
      "total_operating_income",
      "total_revenue",
      "revenue",
    ]),
    operatingProfit: pickNumber(record, [
      "income_statement__ebit",
      "operating_profit",
      "operating_result",
      "ebit",
    ]),
    netIncome: pickNumber(record, [
      "income_statement__net_income",
      "net_income",
      "ordinary_result",
      "net_result",
      "result",
    ]),
    totalAssets: pickNumber(record, ["balance__total_assets", "total_assets", "sum_assets"]),
    equity: pickNumber(record, ["balance__equity", "total_equity", "sum_equity", "equity"]),
    cashAndDeposits: pickNumber(record, ["balance__cash_and_deposits", "cash_and_deposits"]),
    liabilities: pickNumber(record, ["balance__liabilities", "total_liabilities", "sum_debt"]),
  };
  const hasAny = Object.values(financials).some((v) => v !== null);
  return hasAny ? financials : null;
}

/** Reporting currency inferred from the country prefix of the Enin companyId. */
export function currencyForCompanyId(companyId: string): string | null {
  const prefix = companyId.slice(0, 2);
  const byCountry: Record<string, string> = { NO: "NOK", SE: "SEK", DK: "DKK", FI: "EUR" };
  return byCountry[prefix] ?? null;
}

// ─── Ultimate ownership ──────────────────────────────────────────────────────

export type EninOwner = {
  name: string;
  /** 0–100, or null when no stake field was found. */
  stakePercent: number | null;
  /** True/false when the row identifies the shareholder kind; null = unknown. */
  isPerson: boolean | null;
};

const MAX_OWNERS = 8;

export function extractOwners(payload: EninLookupBundle["share_ownership_ultimate"]): EninOwner[] {
  const rows = unwrapRows(payload, ["company_share_ownership_ultimate_collection", "data"]);
  const owners: EninOwner[] = [];
  for (const row of rows) {
    if (!isRecord(row)) continue;
    const name = pickString(row, [
      "shareholder_name",
      "shareholder_company.name",
      "shareholder_person.name",
      "company.name",
      "person.name",
      "name",
    ]);
    if (!name) continue;
    const stake = pickNumber(row, [
      "ultimate_ownership_percent",
      "ownership_percent",
      "ownership_share",
      // Live 2026-07 shape: `ownership` as a 0–1 fraction (rescaled below).
      "ownership",
      "share",
      "stake",
      "percent",
    ]);
    const personKey = pickString(row, [
      "shareholder_person_uuid",
      "shareholder_person.name",
      "person.name",
    ]);
    const companyKey = pickString(row, [
      "shareholder_company_uuid",
      "shareholder_company.name",
      "company.name",
    ]);
    owners.push({
      name,
      stakePercent: stake,
      isPerson: personKey ? true : companyKey ? false : null,
    });
  }
  // Enin may report stakes as 0–1 fractions; rescale when the whole list fits in [0, 1].
  const stakes = owners.map((o) => o.stakePercent).filter((s): s is number => s !== null);
  if (stakes.length > 0 && stakes.every((s) => s <= 1)) {
    for (const owner of owners) {
      if (owner.stakePercent !== null) owner.stakePercent *= 100;
    }
  }
  owners.sort((a, b) => (b.stakePercent ?? -1) - (a.stakePercent ?? -1));
  return owners.slice(0, MAX_OWNERS);
}

// ─── Person/company roles ────────────────────────────────────────────────────

export type EninRole = {
  personName: string;
  role: string | null;
  active: boolean;
  /** ISO date (YYYY-MM-DD) when the payload carries it — sharpens PEP searches. */
  birthDate: string | null;
};

const MAX_ROLES = 10;

export function extractRoles(payload: EninLookupBundle["person_company_role"]): EninRole[] {
  const rows = unwrapRows(payload, ["person_company_role_collection", "data"]);
  const roles: EninRole[] = [];
  for (const row of rows) {
    if (!isRecord(row)) continue;
    const personName = pickString(row, [
      "person.full_name",
      "person.name",
      "full_name",
      "person_name",
      "name",
    ]);
    if (!personName) continue;
    const role = pickString(row, [
      // Live 2026-07 shape: company_role.company_role_key ("ceo", "board_chairman", …).
      "company_role.company_role_key",
      "person_company_role.person_company_role_type",
      "person_company_role_type",
      "role_type",
      "role_title",
      "role",
      "title",
    ]);
    const endDate = pickString(row, [
      "person_company_role.to_date",
      "person_company_role.valid_to",
      "valid_to",
      "to_date",
      "role_end_date",
      "end_date",
    ]);
    const birthDateRaw = pickString(row, ["person.birth_date", "birth_date"]);
    roles.push({
      personName,
      role,
      active: endDate === null,
      birthDate: birthDateRaw ? birthDateRaw.slice(0, 10) : null,
    });
  }
  return roles.slice(0, MAX_ROLES);
}

// ─── AML screening candidates (QP-184 — UBO / key-role / representative) ─────

/**
 * Beneficial owners (hvitvaskingsloven §14 / AMLD art. 3(6)): the Enin
 * beneficial-ownership register rows. Rows are UBOs by definition — stake is
 * carried for display only.
 */
export function extractBeneficialOwners(payload: unknown): EninOwner[] {
  const rows = unwrapRows(payload, ["company_share_ownership_beneficial_collection", "data"]);
  const owners: EninOwner[] = [];
  for (const row of rows) {
    if (!isRecord(row)) continue;
    const name = pickString(row, [
      "shareholder_person.name",
      "person.name",
      "beneficial_owner_name",
      "shareholder_name",
      "name",
    ]);
    if (!name) continue;
    const stake = pickNumber(row, [
      "beneficial_ownership_percent",
      "ultimate_ownership_percent",
      "ownership_percent",
      "share",
      "percent",
    ]);
    owners.push({ name, stakePercent: stake, isPerson: true });
  }
  const stakes = owners.map((o) => o.stakePercent).filter((v): v is number => v !== null);
  if (stakes.length > 0 && stakes.every((v) => v <= 1)) {
    for (const owner of owners) {
      if (owner.stakePercent !== null) owner.stakePercent *= 100;
    }
  }
  return owners;
}

export type EninScreeningCandidate = {
  name: string;
  kind: "ubo" | "role" | "representative";
  /** Display detail: stake for UBOs, role title for roles/representatives. */
  detail: string | null;
  /** ISO birth date when known — passed to the PEP search for precision. */
  birthDate: string | null;
};

const UBO_STAKE_THRESHOLD = 25;
const SENIOR_ROLE_PATTERN = /ceo|chief executive|daglig leder|chair|styreleder|styrets leder/i;

/**
 * Derive the AML screening set for a company:
 * - beneficial owners (register UBOs), plus ultimate person-owners ≥25%
 * - fallback when no UBO is identifiable: active senior roles (CEO / chair),
 *   per AMLD art. 3(6)(a)(ii)
 * - every registered acting representative (QL-654: there can be several)
 * Deduped case-insensitively by name; first occurrence wins.
 */
export function deriveScreeningCandidates(
  bundle: EninLookupBundle,
  beneficialPayload: unknown,
  representatives: ReadonlyArray<{ name: string }>,
): EninScreeningCandidate[] {
  const candidates: EninScreeningCandidate[] = [];
  const seen = new Set<string>();
  const add = (candidate: EninScreeningCandidate) => {
    const key = candidate.name.trim().toLowerCase();
    if (!key || seen.has(key)) return;
    seen.add(key);
    candidates.push(candidate);
  };

  const formatStake = (stake: number | null) =>
    stake !== null ? `${Math.round(stake * 10) / 10} %` : null;

  for (const owner of extractBeneficialOwners(beneficialPayload)) {
    add({
      name: owner.name,
      kind: "ubo",
      detail: formatStake(owner.stakePercent),
      birthDate: null,
    });
  }
  for (const owner of extractOwners(bundle.share_ownership_ultimate)) {
    if (owner.isPerson === false) continue;
    if (owner.stakePercent === null || owner.stakePercent < UBO_STAKE_THRESHOLD) continue;
    add({
      name: owner.name,
      kind: "ubo",
      detail: formatStake(owner.stakePercent),
      birthDate: null,
    });
  }

  if (candidates.length === 0) {
    for (const role of extractRoles(bundle.person_company_role)) {
      if (!role.active || !role.role || !SENIOR_ROLE_PATTERN.test(role.role)) continue;
      add({ name: role.personName, kind: "role", detail: role.role, birthDate: role.birthDate });
    }
  }

  for (const representative of representatives) {
    if (!representative.name) continue;
    add({ name: representative.name, kind: "representative", detail: null, birthDate: null });
  }

  return candidates;
}
